import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, LinearProgress, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/EnterpriseAuthContext';
import { useApp } from '../context/AppContext';
import { useAIRequests } from '../context/AIRequestContext';
import { useUsage } from '../context/UsageContext';
import { hasReachedLimit } from '../services/usageService';
import edgeFunctionService from '../services/edgeFunctionService';
import { parseGoGenerateParams, resolveGoGenerateTarget } from '../services/autopilotModel';
import { openDocumentEditor, sessionPath } from '../utils/navigation';
import useGenerationRequest from './documentGenerator/useGenerationRequest';
import { PageContainer, SectionCard } from './pagekit';
import { InkyWorking } from './brand';
import { SETTINGS_TAB_PRICING } from './SettingsDialog';

/** Same default tone as the document generator. */
const DEFAULT_TONE = 'The Scholar';
/** A click within this window reuses the generation already started for the same meeting and template. */
export const GO_GENERATE_REUSE_MS = 15 * 60 * 1000;
const RUN_STORAGE_PREFIX = 'quillai:go-generate:';
/** Longest wait for the new workspace's data after a switch before checking anyway. */
const SWITCH_LOAD_WAIT_MS = 10000;

/** Messages for each reason the link cannot run. */
const BLOCKED_COPY = {
  bad_link: 'This link is not complete. Open the meeting in Quill and pick the document there.',
  not_found: "We couldn't find that meeting, or you don't have access to it.",
  other_account: 'This meeting is in another workspace.',
  unfiled: 'This meeting is not filed under a project yet. File it first, then create the document.',
  no_project_access: "You don't have access to this meeting's project.",
  template_missing: 'That template is no longer in this workspace.',
  template_not_ready: 'That template is still importing, or its import failed. Try again when it is ready.',
  limit: "You've reached your plan's document limit for this month.",
  failed: "We couldn't start the document. Please try again.",
};

/**
 * Remembered run for this meeting and template in this browser tab.
 * @param {string} key - Storage key.
 * @returns {string|null} Request id when recent.
 */
function readRememberedRun(key) {
  try {
    const raw = JSON.parse(window.sessionStorage.getItem(key) || 'null');
    if (raw && typeof raw.requestId === 'string' && Date.now() - Number(raw.at || 0) < GO_GENERATE_REUSE_MS) return raw.requestId;
  } catch {
    // Storage blocked or bad JSON: start fresh.
  }
  return null;
}

/**
 * Remember a run for this meeting and template in this tab.
 * @param {string} key - Storage key.
 * @param {string} requestId - Request id.
 * @returns {void}
 */
function rememberRun(key, requestId) {
  try {
    window.sessionStorage.setItem(key, JSON.stringify({ requestId, at: Date.now() }));
  } catch {
    // Best effort.
  }
}

/**
 * A generation this user already started from this meeting and template a moment ago (another
 * tab, a double click in the email), so a second click does not create a second document.
 * @param {object} args - Arguments.
 * @returns {Promise<object|null>} ai_requests row.
 */
async function findRecentRun({ userId, projectId, templateId, sessionId }) {
  const { data, error } = await supabase
    .from('ai_requests')
    .select('id, status, request_data, result_data, created_at')
    .eq('user_id', userId)
    .eq('type', 'document')
    .eq('project_id', projectId)
    .eq('template_id', templateId)
    .order('created_at', { ascending: false })
    .limit(5);
  if (error || !Array.isArray(data)) return null;
  return data.find((row) =>
    row.request_data?.origin?.sessionId === sessionId &&
    !['failed', 'cancelled'].includes(row.status) &&
    Date.now() - new Date(row.created_at || 0).getTime() < GO_GENERATE_REUSE_MS
  ) || null;
}

/**
 * `/go/generate?session=<id>&template=<id>&source=email|app`: the one-click link from the
 * meeting recap email and "Ready for you". Checks the meeting, its project and the template
 * against what this user can access, starts the normal generation for that project and template
 * (all of the project's evidence, default tone), shows "Creating your <template>...", then opens
 * the document. A repeat click reuses the run it already started.
 * @returns {JSX.Element} Page.
 */
export default function GoGeneratePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { state: authState, actions: authActions } = useAuth();
  const { state: appState, actions: appActions } = useApp();
  const { createRequest, cancelRequest } = useAIRequests();
  const { refreshUsage } = useUsage();

  const link = useMemo(() => parseGoGenerateParams(searchParams), [searchParams]);
  const [phase, setPhase] = useState(link.ok ? 'checking' : 'blocked');
  const [blocked, setBlocked] = useState(link.ok ? null : { reason: 'bad_link' });
  const [target, setTarget] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const startedRef = useRef('');
  const openedRef = useRef(false);

  const activeAccountId = authState.activeAccount?.id || null;
  const userId = authState.user?.id || null;

  // After "Switch workspace" the page stays mounted while AppContext clears the old workspace's
  // lists and loads the new one's. Wait for that load (loading goes on, then off) before checking
  // again, so the check never reads the old or empty lists.
  const [seenAccountId, setSeenAccountId] = useState(activeAccountId);
  const [awaitingAccountId, setAwaitingAccountId] = useState(null);
  const sawLoadingRef = useRef(false);
  if (activeAccountId !== seenAccountId) {
    setSeenAccountId(activeAccountId);
    if (seenAccountId && activeAccountId) {
      sawLoadingRef.current = false;
      setAwaitingAccountId(activeAccountId);
      setPhase('checking');
      setBlocked(null);
    }
  }
  useEffect(() => {
    if (!awaitingAccountId) return undefined;
    if (appState.loading) {
      sawLoadingRef.current = true;
      return undefined;
    }
    if (sawLoadingRef.current) {
      setAwaitingAccountId(null);
      return undefined;
    }
    // The load normally starts within a moment; never wait forever.
    const timer = setTimeout(() => setAwaitingAccountId(null), SWITCH_LOAD_WAIT_MS);
    return () => clearTimeout(timer);
  }, [awaitingAccountId, appState.loading]);

  const ready = Boolean(link.ok && activeAccountId && userId && appState.initialLoadComplete && !awaitingAccountId);
  const generation = useGenerationRequest(requestId);

  useEffect(() => {
    if (!ready) return undefined;
    const runKey = `${activeAccountId}:${link.sessionId}:${link.templateId}:${attempt}`;
    if (startedRef.current === runKey) return undefined;
    startedRef.current = runKey;
    let cancelled = false;

    (async () => {
      setPhase('checking');
      setBlocked(null);
      const { data: session, error } = await supabase
        .from('sessions')
        .select('id, name, project_id, account_id')
        .eq('id', link.sessionId)
        .maybeSingle();
      if (cancelled) return;
      const decision = resolveGoGenerateTarget({
        session: error ? null : session,
        activeAccountId,
        projects: appState.projects || [],
        templates: appState.templates || [],
        templateId: link.templateId,
      });
      if (!decision.ok) {
        setBlocked(decision);
        setPhase('blocked');
        return;
      }
      setTarget({ ...decision, session });

      const storageKey = `${RUN_STORAGE_PREFIX}${link.sessionId}:${link.templateId}`;
      const remembered = readRememberedRun(storageKey);
      const recent = remembered ? { id: remembered } : await findRecentRun({
        userId,
        projectId: decision.project.id,
        templateId: decision.template.id,
        sessionId: link.sessionId,
      });
      if (cancelled) return;
      if (recent?.id) {
        rememberRun(storageKey, recent.id);
        setRequestId(recent.id);
        setPhase('running');
        return;
      }

      const usage = await refreshUsage();
      if (cancelled) return;
      if (usage && hasReachedLimit(usage, 'documents')) {
        setBlocked({ reason: 'limit' });
        setPhase('blocked');
        return;
      }

      setPhase('starting');
      const documentName = `${decision.project.name} - ${decision.template.name}`;
      let request = null;
      try {
        request = await createRequest({
          type: 'document',
          title: documentName,
          projectId: decision.project.id,
          templateId: decision.template.id,
          priority: 0,
          data: {
            tone: DEFAULT_TONE,
            contentSource: 'requirements',
            existingDocumentId: null,
            variant: 0,
            origin: { kind: 'meeting_autopilot', sessionId: link.sessionId, source: link.source },
          },
        });
        rememberRun(storageKey, request.id);
        await edgeFunctionService.generateDocument({
          requestId: request.id,
          projectId: decision.project.id,
          templateId: decision.template.id,
          documentName,
          tone: DEFAULT_TONE,
          contentSource: 'requirements',
          existingDocumentId: null,
          variant: 0,
        });
        refreshUsage();
        if (cancelled) return;
        setRequestId(request.id);
        setPhase('running');
      } catch {
        if (request?.id) {
          try {
            await cancelRequest(request.id);
          } catch {
            // The notifications panel shows it; nothing else to do here.
          }
          try {
            window.sessionStorage.removeItem(storageKey);
          } catch {
            // Best effort.
          }
        }
        if (!cancelled) {
          setBlocked({ reason: 'failed' });
          setPhase('blocked');
        }
      }
    })();

    return () => {
      cancelled = true;
      // Let a remount (StrictMode, account switch) run the checks again.
      startedRef.current = '';
    };
    // appState lists are read once when ready; a later reload must not restart the run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, activeAccountId, link, attempt]);

  // Open the document once it exists.
  const finishedDoc = generation.document;
  const failedRun = generation.request?.status === 'failed' || generation.request?.status === 'cancelled';
  useEffect(() => {
    if (!finishedDoc?.id || openedRef.current) return;
    openedRef.current = true;
    if (typeof appActions?.upsertGeneratedDocumentLocal === 'function') {
      try {
        appActions.upsertGeneratedDocumentLocal(finishedDoc);
      } catch {
        // The editor loads the document itself.
      }
    }
    openDocumentEditor(navigate, {
      documentId: finishedDoc.id,
      projectId: finishedDoc.project_id || target?.project?.id,
      title: finishedDoc.name,
      replace: true,
    });
  }, [finishedDoc, appActions, navigate, target]);

  const handleRetry = useCallback(() => {
    try {
      window.sessionStorage.removeItem(`${RUN_STORAGE_PREFIX}${link.sessionId}:${link.templateId}`);
    } catch {
      // Best effort.
    }
    openedRef.current = false;
    setRequestId(null);
    setAttempt((n) => n + 1);
  }, [link]);

  const templateName = target?.template?.name || (appState.templates || []).find((t) => t.id === link.templateId)?.name || 'document';
  const otherAccount = blocked?.reason === 'other_account'
    ? (authState.accounts || []).find((a) => a.id === blocked.accountId) || null
    : null;

  let body;
  if (phase === 'blocked') {
    const reason = blocked?.reason || 'failed';
    body = (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Alert severity={reason === 'failed' ? 'error' : 'warning'}>
          {BLOCKED_COPY[reason] || BLOCKED_COPY.failed}
          {otherAccount ? ` Switch to ${otherAccount.name || 'that workspace'} to continue.` : ''}
        </Alert>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {otherAccount && (
            <Button variant="contained" onClick={() => authActions.switchAccount(otherAccount.id)}>
              Switch workspace
            </Button>
          )}
          {reason === 'limit' && (
            <Button
              variant="contained"
              onClick={() => window.dispatchEvent(new CustomEvent('quillai:open-settings', { detail: { initialTab: SETTINGS_TAB_PRICING } }))}
            >
              See plans
            </Button>
          )}
          {['failed', 'no_project_access', 'template_missing'].includes(reason) && (
            <Button variant={reason === 'failed' ? 'contained' : 'outlined'} onClick={handleRetry}>Try again</Button>
          )}
          {link.ok && ['unfiled', 'template_missing', 'template_not_ready', 'limit', 'failed'].includes(reason) && (
            <Button variant="outlined" onClick={() => navigate(sessionPath(link.sessionId, 'summary'))}>Open the meeting</Button>
          )}
          <Button onClick={() => navigate('/home')}>Go to Home</Button>
        </Box>
      </Box>
    );
  } else if (failedRun) {
    body = (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Alert severity="error">
          {generation.request?.error_message ? `The document could not be created: ${generation.request.error_message}` : BLOCKED_COPY.failed}
        </Alert>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="contained" onClick={handleRetry}>Try again</Button>
          <Button onClick={() => navigate(sessionPath(link.sessionId, 'summary'))}>Open the meeting</Button>
        </Box>
      </Box>
    );
  } else if (generation.error) {
    body = (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Alert severity="warning">{generation.error}</Alert>
        {target?.project?.id && (
          <Button variant="contained" onClick={() => navigate(`/projects/${target.project.id}?tab=documents`)} sx={{ justifySelf: 'start' }}>
            Open the project
          </Button>
        )}
      </Box>
    );
  } else {
    const progress = Number(generation.request?.progress);
    const status = phase === 'checking' ? 'Checking the meeting and template' : generation.request?.progress_message || 'Starting';
    body = (
      <Box sx={{ display: 'grid', gap: 2, justifyItems: 'center', textAlign: 'center', py: 3 }} role="status" aria-live="polite">
        <InkyWorking label={`Creating your ${templateName}...`} mood="thinking" />
        <Typography variant="body2" color="text.secondary">{status}</Typography>
        {phase === 'running' && Number.isFinite(progress) && progress > 0 ? (
          <LinearProgress variant="determinate" value={Math.min(progress, 100)} sx={{ width: '100%', maxWidth: 360 }} aria-label="Progress" />
        ) : (
          <CircularProgress size={20} aria-hidden="true" />
        )}
        <Typography variant="body2" color="text.secondary">
          It opens here when it is ready. You can leave this page; it also appears in Documents.
        </Typography>
      </Box>
    );
  }

  return (
    <PageContainer width="narrow">
      <SectionCard title={phase === 'blocked' ? 'Create a document' : `Creating your ${templateName}...`}>
        {body}
      </SectionCard>
    </PageContainer>
  );
}
